import React from 'react';
import useTheme from '../../../hooks/useTheme';
import './ThemeSelector.css';

const OPTIONS = [
    {
        value: 'light',
        label: 'Claro',
        description: 'Interface clara, ideal para ambientes iluminados.',
    },
    {
        value: 'dark',
        label: 'Escuro',
        description: 'Interface escura, confortável em ambientes com pouca luz.',
    },
    {
        value: 'auto',
        label: 'Automático',
        description: 'Acompanha o tema do seu sistema operacional.',
    },
];

function ThemePreview({ variant }) {

    const isDark = variant === 'dark';
    const isAuto = variant === 'auto';

    const bg       = isDark ? '#1E1E20' : '#F2F2F4';
    const sb       = isDark ? '#252528' : '#ECECEE';
    const card     = isDark ? '#2C2C2E' : '#FFFFFF';
    const stroke   = isDark ? '#3A3A3C' : '#E0E0E2';
    const text     = isDark ? '#FFFFFF' : '#1A1A1A';
    const textDim  = isDark ? '#98989D' : '#86868B';
    const accent   = '#0A84FF';

    if (isAuto) {

        return (
            <svg viewBox="0 0 200 130" className="theme-preview-svg" aria-hidden="true">
                <defs>
                    <clipPath id="auto-light-clip"><polygon points="0,0 200,0 0,130" /></clipPath>
                    <clipPath id="auto-dark-clip"><polygon points="200,0 200,130 0,130" /></clipPath>
                </defs>

                <g clipPath="url(#auto-light-clip)">
                    <rect x="0" y="0" width="200" height="130" fill="#F2F2F4" />
                    <rect x="0" y="0" width="56" height="130" fill="#ECECEE" />
                    <rect x="6" y="10" width="44" height="6" rx="2" fill="#86868B" opacity="0.4" />
                    <rect x="6" y="22" width="44" height="5" rx="2" fill="#86868B" opacity="0.3" />
                    <rect x="6" y="31" width="44" height="5" rx="2" fill="#86868B" opacity="0.3" />
                    <rect x="64" y="10" width="130" height="8" rx="2" fill="#1A1A1A" opacity="0.85" />
                    <rect x="64" y="26" width="130" height="40" rx="6" fill="#FFFFFF" stroke="#E0E0E2" />
                    <rect x="64" y="72" width="130" height="40" rx="6" fill="#FFFFFF" stroke="#E0E0E2" />
                </g>

                <g clipPath="url(#auto-dark-clip)">
                    <rect x="0" y="0" width="200" height="130" fill="#1E1E20" />
                    <rect x="0" y="0" width="56" height="130" fill="#252528" />
                    <rect x="6" y="10" width="44" height="6" rx="2" fill="#98989D" opacity="0.5" />
                    <rect x="6" y="22" width="44" height="5" rx="2" fill="#98989D" opacity="0.4" />
                    <rect x="6" y="31" width="44" height="5" rx="2" fill="#98989D" opacity="0.4" />
                    <rect x="64" y="10" width="130" height="8" rx="2" fill="#FFFFFF" opacity="0.9" />
                    <rect x="64" y="26" width="130" height="40" rx="6" fill="#2C2C2E" stroke="#3A3A3C" />
                    <rect x="64" y="72" width="130" height="40" rx="6" fill="#2C2C2E" stroke="#3A3A3C" />
                </g>

                <line x1="200" y1="0" x2="0" y2="130" stroke="rgba(255,255,255,0.6)" strokeWidth="0.5" />
            </svg>
        );
    }

    return (
        <svg viewBox="0 0 200 130" className="theme-preview-svg" aria-hidden="true">

            <rect x="0" y="0" width="200" height="130" fill={bg} />

            <rect x="0" y="0" width="56" height="130" fill={sb} />
            <rect x="56" y="0" width="0.5" height="130" fill={stroke} />

            <circle cx="14" cy="14" r="6" fill={accent} />
            <rect x="6" y="30" width="44" height="5" rx="2" fill={accent} opacity="0.9" />
            <rect x="6" y="40" width="44" height="5" rx="2" fill={textDim} opacity="0.5" />
            <rect x="6" y="50" width="44" height="5" rx="2" fill={textDim} opacity="0.5" />
            <rect x="6" y="60" width="44" height="5" rx="2" fill={textDim} opacity="0.5" />

            <circle cx="14" cy="115" r="5" fill={accent} />
            <rect x="22" y="113" width="28" height="3" rx="1" fill={text} opacity="0.7" />
            <rect x="22" y="118" width="20" height="2.5" rx="1" fill={textDim} opacity="0.5" />

            <rect x="64" y="10" width="80" height="8" rx="2" fill={text} opacity="0.9" />
            <rect x="64" y="22" width="50" height="4" rx="1" fill={textDim} opacity="0.6" />

            <rect x="64" y="32" width="40" height="22" rx="4" fill={card} stroke={stroke} />
            <rect x="108" y="32" width="40" height="22" rx="4" fill={card} stroke={stroke} />
            <rect x="152" y="32" width="40" height="22" rx="4" fill={card} stroke={stroke} />

            <rect x="64" y="60" width="128" height="56" rx="6" fill={card} stroke={stroke} />
            <rect x="72" y="68" width="60" height="4" rx="1" fill={text} opacity="0.8" />
            <rect x="72" y="78" width="110" height="3" rx="1" fill={textDim} opacity="0.5" />
            <rect x="72" y="86" width="100" height="3" rx="1" fill={textDim} opacity="0.5" />
            <rect x="72" y="94" width="80"  height="3" rx="1" fill={textDim} opacity="0.5" />
            <rect x="72" y="104" width="40" height="6" rx="3" fill={accent} />
        </svg>
    );
}

function ThemeSelector({ className = '' }) {
    const { theme, setTheme, effective } = useTheme();

    return (
        <div className={`theme-selector ${className}`.trim()}>
            <div className="theme-selector-cards">
                {OPTIONS.map((opt) => {
                    const isSelected = theme === opt.value;
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            className={`theme-selector-card ${isSelected ? 'is-selected' : ''}`}
                            onClick={() => setTheme(opt.value)}
                            aria-pressed={isSelected}
                            aria-label={`Tema ${opt.label}`}
                        >
                            <div className="theme-selector-preview">
                                <ThemePreview variant={opt.value} />
                                {isSelected && (
                                    <span className="theme-selector-check" aria-hidden="true">
                                        <svg viewBox="0 0 16 16" width="14" height="14"><polyline points="3.5 8.5 7 12 13 5" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                    </span>
                                )}
                            </div>
                            <div className="theme-selector-card-info">
                                <div className="theme-selector-card-title">
                                    {opt.label}
                                    {opt.experimental && <span className="theme-selector-badge">Beta</span>}
                                </div>
                                <div className="theme-selector-card-desc">{opt.description}</div>
                            </div>
                        </button>
                    );
                })}
            </div>

            <div className="theme-selector-meta">
                <span>
                    Aplicado: <strong>{effective === 'dark' ? 'Escuro' : 'Claro'}</strong>
                    {theme === 'auto' && <em> (Automático — segue o seu sistema)</em>}
                </span>
            </div>
        </div>
    );
}

export default ThemeSelector;
